import { useTranslation } from 'react-i18next'

import {
  GetPrivateRegionLeasedTimeMetricsRes,
  GetPrivateRegionStatsNodesListRes,
  GetPrivateRegionStatsOverviewRes,
} from '@/api/type-response'
import { EmptyView, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { Pagination } from '@/components/base/Pagination'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { formatDatePreset } from '@/utils/formatter'
import { formatGB } from '@/utils/storage'

import { NodeDetailContent } from './NodeDetailContent'
import { NodeStatusCard } from './NodeStatusCard'
import { StatusBadge } from './StatusBadge'
import UsageTimeCard from './UsageTimeCard'

interface ComputeNodeContentProps {
  computeNodes: GetPrivateRegionStatsNodesListRes | null
  models: string[] | null
  page: number
  pageSize: number
  filters: { gpuModel?: string | number }
  onPageChange: (page: number) => void
  onFiltersChange: (filters: { gpuModel?: string | number }) => void
  overviewData: GetPrivateRegionStatsOverviewRes | null
  usageMetrics: GetPrivateRegionLeasedTimeMetricsRes | null
  onRefetch?: () => void
}

export function ComputeNodeContent({
  computeNodes,
  models,
  page,
  pageSize,
  filters,
  onPageChange,
  onFiltersChange,
  overviewData,
  usageMetrics,
  onRefetch,
}: ComputeNodeContentProps) {
  const { t } = useTranslation('privateRegion')
  const { details, pagination } = computeNodes || { details: [], pagination: null }

  // Generate GPU model options from API models list with "All" option
  const gpuModelOptions = [
    { label: t('selector.allGpuModels'), value: '' },
    ...(models || []).map((model) => ({ label: model, value: model })),
  ]

  // Prepare data for statistics cards
  const hasStatsData = overviewData && usageMetrics

  let nodeStatusData: { status: 'online' | 'standby'; count: number; label: string }[] = []
  let actualUsageData: { date: string; value: number }[] = []
  let occupancyRateData: { date: string; value: number }[] = []
  let leasedTimeHour = 0
  let leasedTimeHourRateDelta = 0
  let occupancyRate = 0
  let occupancyRateDelta = 0
  let nodesAll = 0

  if (hasStatsData) {
    const { nodesAll: totalNodes, nodesOnline } = overviewData
    const standbyNodes = totalNodes - nodesOnline

    leasedTimeHour = overviewData.leasedTimeHour
    leasedTimeHourRateDelta = overviewData.leasedTimeHourRateDelta
    occupancyRate = overviewData.occupancyRate
    occupancyRateDelta = overviewData.occupancyRateDelta
    nodesAll = totalNodes

    nodeStatusData = [
      { status: 'online' as const, count: nodesOnline, label: t('common.online') },
      { status: 'standby' as const, count: standbyNodes, label: t('common.standby') },
    ]

    actualUsageData = usageMetrics.metrics.map((m) => ({
      date: formatDatePreset(m.timestamp * 1000, 'day'),
      value: m.leasedHour,
    }))

    occupancyRateData = usageMetrics.metrics.map((m) => ({
      date: formatDatePreset(m.timestamp * 1000, 'day'),
      value: m.totalHour > 0 ? m.leasedHour / m.totalHour : 0,
    }))
  }

  if (!computeNodes) {
    return (
      <EmptyView
        title={
          <Trans ns="privateRegion" i18nKey="common.noData">
            No data
          </Trans>
        }
      />
    )
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <Dropdown
          placeholder={t('selector.allGpuNodes')}
          options={gpuModelOptions}
          value={filters.gpuModel ?? ''}
          onChange={(v) => onFiltersChange({ ...filters, gpuModel: Array.isArray(v) ? v[0] : v })}
          onClear={() => onFiltersChange({ ...filters, gpuModel: undefined })}
          className="w-60"
        />
      </div>

      {/* Statistics Cards */}
      {hasStatsData && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          <UsageTimeCard
            title={t('usageTime.usageTimeThisMonth')}
            value={leasedTimeHour}
            changePercent={leasedTimeHourRateDelta * 100}
            isPositiveChange={leasedTimeHourRateDelta > 0}
            chartData={actualUsageData}
          />
          <UsageTimeCard
            title={t('usageTime.occupancyRateThisMonth')}
            value={occupancyRate}
            isValuePercent={true}
            changePercent={occupancyRateDelta * 100}
            isPositiveChange={occupancyRateDelta > 0}
            chartData={occupancyRateData}
          />
          <NodeStatusCard nodes={nodeStatusData} total={nodesAll} />
        </div>
      )}

      {/* Table */}
      {details.length > 0 ? (
        <div className="p-6 border border-neutral-200 rounded-2xl">
          <table className="table-card table-fixed">
            <thead>
              <tr>
                <th className="t-sm w-[12%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="computeNode.serverId">
                    Server ID
                  </Trans>
                </th>
                <th className="t-sm w-[14%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="computeNode.name">
                    Name
                  </Trans>
                </th>
                <th className="t-sm w-[12%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="common.region">
                    Region
                  </Trans>
                </th>
                <th className="t-sm w-[10%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="common.status">
                    Status
                  </Trans>
                </th>
                <th className="t-sm w-[18%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="computeNode.gpuType">
                    GPU Type
                  </Trans>
                </th>
                <th className="t-sm w-[18%]" colSpan={1}>
                  <Trans ns="privateRegion" i18nKey="computeNode.cpuType">
                    CPU Type
                  </Trans>
                </th>
                <th className="t-sm w-[12%]" colSpan={1}>
                  <div className="text-right">
                    <Trans ns="privateRegion" i18nKey="computeNode.storage">
                      Storage
                    </Trans>
                  </div>
                </th>
                <th className="t-sm w-[4%]"></th>
              </tr>
            </thead>
            <tbody>
              {details.map((node) => (
                <CollapseTableRow
                  key={node.resID}
                  colSpan={8}
                  tableClassName="!border-none ring-1 ring-neutral-200 !rounded-xl"
                  content={<NodeDetailContent node={node} onRefetch={onRefetch} />}
                >
                  <td className="t-sb w-[12%]" colSpan={1}>
                    {node.resID}
                  </td>
                  <td className="t-sb w-[14%]" colSpan={1}>
                    {node.name}
                  </td>
                  <td className="t-sb w-[12%]">{node.regionInfo.name}</td>
                  <td className="t-sb w-[10%]">
                    <StatusBadge status={node.status} className="w-fit" />
                  </td>
                  <td className="t-sb w-[18%]">{node.gpuModel || '-'}</td>
                  <td className="t-sb w-[18%]">{node.cpuModel || '-'}</td>
                  <td className="t-sb w-[12%]">
                    <div className="text-right">
                      {formatGB(node.diskGB, { dynamicDecimals: true })}
                    </div>
                  </td>
                </CollapseTableRow>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyView
          title={
            <Trans ns="privateRegion" i18nKey="common.noData">
              No data
            </Trans>
          }
        />
      )}

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <Pagination
          size={pageSize}
          total={pagination.total}
          current={page}
          onChange={onPageChange}
          className="justify-end mt-6"
        />
      )}
    </div>
  )
}
